/**
 * The World Map's data (lane AK, plan 22): places and roads from the authored
 * portal graph, names from the level files, level bands from the zone content,
 * positions from worldMapLayout.ts. Pure: no React, no registry singletons, so
 * the window, the gallery page and the tests build the same model.
 */
import type { PlatformerLevel } from '../../../../shared/CharacterTypes';
import type { ZoneConfig } from '../../../../shared/content/generated/zones.gen';
import { pairLinks, portalPairKey, walkPortalGraph } from '../../../../shared/logic/portalGraph';
import type { PortalLink } from '../../../../shared/logic/portalGraph';
import { isPocketMap, getPocketParent } from '../../../../shared/logic/pocketMaps';
import type { TomeVibeName } from '../../primitives/tome/tomeVibes';
import { zoneTomeVibe } from '../../primitives/tome/zoneTomeVibes';
import type { WorldMapLabelSide, WorldMapLayout, WorldMapRegion } from './worldMapLayout';

export type WorldMapLevelLookup = (mapId: string) => Pick<PlatformerLevel, 'id' | 'platforms' | 'name'> | undefined;
export type WorldMapZoneLookup = (zoneId: string) => Pick<ZoneConfig, 'levelRange' | 'zoneType' | 'draft'> | null | undefined;

export interface WorldMapPlace {
  id: string;
  name: string;
  x: number;
  y: number;
  label: WorldMapLabelSide;
  zoneType: ZoneConfig['zoneType'] | null;
  levelRange: [number, number] | null;
  /** Short band text for the tooltip ("Levels 2–3", "Town"), or null. */
  band: string | null;
  onMainRoute: boolean;
  vibe: TomeVibeName;
}

export interface WorldMapRoad {
  key: string;
  from: string;
  to: string;
  /** Consecutive places on the layout's main route. */
  main: boolean;
  /** Portals lead only from → to (no way back by the same road). */
  oneWay: boolean;
  /**
   * A way along this road is sealed (portalSeal.ts): drawn dim, never hidden.
   * With one open direction `from → to` is that direction; fully sealed roads
   * have no open direction at all (plan 26 F13).
   */
  sealed: boolean;
  /** Drawn as a sea crossing through these cubic control points (plan 28 D54), or null. */
  via: readonly [{ x: number; y: number }, { x: number; y: number }] | null;
}

export interface WorldMapModel {
  title: string;
  view: WorldMapLayout['view'];
  places: WorldMapPlace[];
  roads: WorldMapRoad[];
  /** Exact authored portals, including parallel returns; used to detect stale artwork. */
  portalLinks?: readonly PortalLink[];
  /** Landmasses drawn under the places (illustration only). */
  regions: readonly WorldMapRegion[];
  /** Reachable maps the layout has no position for (not drawn; a test keeps this empty). */
  unplaced: string[];
}

/** Tooltip band: towns say so; otherwise the zone's recommended levels. */
export function levelBandLabel(range: [number, number] | null, zoneType: ZoneConfig['zoneType'] | null): string | null {
  if (zoneType === 'town') return 'Town';
  if (!range) return null;
  const [low, high] = range;
  return low === high ? `Level ${low}` : `Levels ${low}–${high}`;
}

export function resolveMapPresentationId(mapId?: string | null): string | undefined {
  if (!mapId) return undefined;
  return getPocketParent(mapId) ?? mapId;
}

export function buildWorldMap(layout: WorldMapLayout, getLevel: WorldMapLevelLookup, getZone: WorldMapZoneLookup): WorldMapModel {
  const graph = walkPortalGraph(layout.entryMapId, getLevel);
  const mainPairs = new Set(layout.mainRoute.slice(1).map((id, index) => portalPairKey(layout.mainRoute[index], id)));
  const onMain = new Set(layout.mainRoute);

  const places: WorldMapPlace[] = [];
  const unplaced: string[] = [];
  for (const id of graph.maps) {
    // Draft zones are playable but stay off the atlas until finished, like pocket maps.
    if (isPocketMap(id) || getZone(id)?.draft) continue;
    const at = layout.places[id];
    if (!at) {
      unplaced.push(id);
      continue;
    }
    const zone = getZone(id);
    const levelRange = zone?.levelRange ? [zone.levelRange[0], zone.levelRange[1]] as [number, number] : null;
    const zoneType = zone?.zoneType ?? null;
    places.push({
      id,
      name: getLevel(id)?.name?.trim() || id,
      x: at.x,
      y: at.y,
      label: at.label ?? 'below',
      zoneType,
      levelRange,
      band: levelBandLabel(levelRange, zoneType),
      onMainRoute: onMain.has(id),
      vibe: zoneTomeVibe(id),
    });
  }

  const drawn = new Set(places.map(place => place.id));
  const roads: WorldMapRoad[] = pairLinks(graph.links)
    .filter(pair => drawn.has(pair.a) && drawn.has(pair.b) && !(pair.concealedForward && pair.concealedBackward))
    .map(pair => {
      const key = portalPairKey(pair.a, pair.b);
      const [from, to] = pair.forward || (!pair.backward && pair.sealedForward) ? [pair.a, pair.b] : [pair.b, pair.a];
      return {
        key, from, to, main: mainPairs.has(key),
        oneWay: pair.forward !== pair.backward,
        sealed: pair.sealedForward || pair.sealedBackward,
        via: layout.seaCrossings?.[key]?.via ?? null,
      };
    });

  const portalLinks = graph.links.filter(link => drawn.has(link.from) && drawn.has(link.to));
  return { title: layout.title, view: layout.view, places, roads, portalLinks, regions: layout.regions ?? [], unplaced };
}
